import { useEffect, useRef } from 'react';
import { ChatMessage } from '@/hooks/useChat';
import { ChatMessageBubble } from './ChatMessageBubble';
import { ChatInput } from './ChatInput';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Lock, Unlock, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface ChatPanelProps {
  conversationSubject: string;
  conversationStatus: 'open' | 'closed';
  messages: ChatMessage[];
  isLoading: boolean;
  onSendMessage: (content: string) => void;
  onBack?: () => void;
  onClose?: () => void;
  onReopen?: () => void;
  isAdmin?: boolean;
  userName?: string;
}

export function ChatPanel({
  conversationSubject,
  conversationStatus,
  messages,
  isLoading,
  onSendMessage,
  onBack,
  onClose,
  onReopen,
  isAdmin,
  userName
}: ChatPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isClosed = conversationStatus === 'closed';

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b bg-muted/30">
        {onBack && (
          <Button variant="ghost" size="icon" onClick={onBack} className="shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium truncate">{conversationSubject}</h3>
            {isClosed && (
              <Badge variant="secondary" className="shrink-0">
                <Lock className="h-3 w-3 mr-1" />
                Fermée
              </Badge>
            )}
          </div>
          {isAdmin && userName && (
            <p className="text-xs text-muted-foreground">{userName}</p>
          )}
        </div>
        {isAdmin && (
          isClosed ? (
            <Button variant="outline" size="sm" onClick={onReopen}>
              <Unlock className="h-4 w-4 mr-2" />
              Réouvrir
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={onClose}>
              <Lock className="h-4 w-4 mr-2" />
              Fermer
            </Button>
          )
        )}
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <p className="text-sm">Aucun message</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => (
              <ChatMessageBubble key={msg.id} message={msg} />
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      {isClosed ? (
        <div className="p-4 border-t bg-muted/30 text-center text-sm text-muted-foreground">
          Cette conversation est fermée
        </div>
      ) : (
        <ChatInput onSend={onSendMessage} />
      )}
    </div>
  );
}
